import { useEffect, useRef } from 'react';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { type ICallNotification } from '@/types/INotification';
import { type ICallPayload } from '@/types/ICallPayload';
import { getCallSummary } from '@/utils/getCallSummary';
import { CALL_TOAST_LIFETIME_MS } from '@/constants/socketTimings';
import CrossIcon from '@/assets/icon/components/CrossIcon';
import { TOAST_VARIANTS, TOAST_TRANSITION } from '../../toastMotion';
import styles from './callToastCard.module.scss';

interface ICallToastCardProps {
  notification: ICallNotification;
  onOpen: (payload: ICallPayload) => void;
  onClose: (id: string) => void;
}

/**
 * Карточка звонка в общем стеке тостов. Никогда не схлопывается с карточками
 * заказов и оформлена фирменным розовым. Клик открывает модалку звонка.
 *
 * `layout`-анимация намеренно не включена: при нескольких карточках она гоняет
 * их через `transform: scale`, а масштабирование визуально сплющивает
 * скругление углов — низ последней карточки выглядит срезанным. Тот же приём
 * уже применён в `OrderToastCard` внутри схлопнутого списка. Появление и уход
 * карточек анимирует `AnimatePresence` через `TOAST_VARIANTS`.
 */
export const CallToastCard: React.FC<ICallToastCardProps> = ({
  notification,
  onOpen,
  onClose
}) => {
  const { payload } = notification;
  const { title, subtitle } = getCallSummary(payload);
  const isMissed = payload.source === 'missed';
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = (): void => {
    timerRef.current = setTimeout(() => {
      onClose(notification.id);
    }, CALL_TOAST_LIFETIME_MS);
  };

  // Повторное событие по тому же номеру приходит как upsert: id не меняется,
  // меняется occurredAt — на него и перезапускаем таймер жизни карточки.
  useEffect(() => {
    startTimer();
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload.occurredAt]);

  const handleOpen = (): void => {
    clearTimer();
    onOpen(payload);
    onClose(notification.id);
  };

  const handleClose = (event: React.MouseEvent): void => {
    event.stopPropagation();
    clearTimer();
    onClose(notification.id);
  };

  return (
    <motion.div
      variants={TOAST_VARIANTS}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={TOAST_TRANSITION}
      className={cn(styles['call-toast-card'], {
        [styles['call-toast-card_source-missed']]: isMissed
      })}
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          handleOpen();
        }
      }}
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
    >
      <button
        type="button"
        className={styles['call-toast-card__close']}
        onClick={handleClose}
        aria-label="Закрыть уведомление о звонке"
      >
        <CrossIcon width={16} height={16} color="currentColor" />
      </button>

      <span className={styles['call-toast-card__title']}>{title}</span>
      <span className={styles['call-toast-card__subtitle']}>{subtitle}</span>
    </motion.div>
  );
};
